import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.faktury` tRPC router.
 *
 * Splatnost je omezena na 0 | 14 dní (F-02 z IT Zadání).
 * DPH sazba povolená: 0 | 10 | 15 | 21 %.
 */

export const fakturaStavEnum = z.enum([
  "KONCEPT",
  "VYSTAVENA",
  "ODESLANA",
  "ZAPLACENA",
  "PO_SPLATNOSTI",
  "STORNOVANA",
  "DOBROPIS",
]);
export type FakturaStav = z.infer<typeof fakturaStavEnum>;

/** Splatnost dní — F-02: 14 dní standard, 0 ihned (<14 dní před seminářem). */
export const splatnostDniSchema = z.union([z.literal(0), z.literal(14)]);

/** DPH sazby platné v ČR (0 osvobozeno, 12 snížená od 2024, 21 základní; 10/15 historické). */
export const dphPctSchema = z.union([
  z.literal(0),
  z.literal(10),
  z.literal(12),
  z.literal(15),
  z.literal(21),
]);

/**
 * Vstup pro vystavení faktury. Cena se dohledá z přihlášky + produktu
 * (po slevě) v routeru.
 */
export const issueFakturaSchema = z.object({
  prihlaskaId: z.string().uuid("Neplatné ID přihlášky"),
  splatnostDni: z.coerce
    .number()
    .int()
    .refine((v) => v === 0 || v === 14, {
      message: "Splatnost musí být 0 nebo 14 dní",
    })
    .optional(),
  dphPct: z.coerce
    .number()
    .refine((v) => [0, 10, 12, 15, 21].includes(v), {
      message: "DPH musí být 0, 10, 12, 15 nebo 21 %",
    })
    .optional(),
  poznamka: z.string().max(2000, "Poznámka musí být 2000 znaků nebo méně").optional(),
});

/** Označení faktury jako zaplacené. */
export const markPaidSchema = z.object({
  id: z.string().uuid("Neplatné ID faktury"),
  zaplacenaAt: z.coerce.date().optional(),
});

/** Vytvoření dobropisu k existující faktuře. */
export const createDobropisSchema = z.object({
  fakturaId: z.string().uuid("Neplatné ID faktury"),
  reason: z
    .string()
    .min(1, "Důvod dobropisu je povinný")
    .max(2000, "Důvod musí být 2000 znaků nebo méně"),
});

/**
 * Whitelist řazení faktur — router mapuje klíče na sloupce. `terminDatum`
 * (datum semináře) zůstává i serverovým fallbackem, když `sortBy` chybí —
 * kontrola úhrad jde chronologicky po akcích.
 */
export const FAKTURY_SORT_KEYS = [
  "cislo",
  "vystaveno",
  "splatnost",
  "castka",
  "stav",
  "terminDatum",
] as const;
export type FakturySortKey = (typeof FAKTURY_SORT_KEYS)[number];

/** List faktur s filtry + paginací. */
export const listFakturySchema = paginationSchema
  .merge(sortSchema(FAKTURY_SORT_KEYS, "desc"))
  .extend({
    stav: fakturaStavEnum.optional(),
    prihlaskaId: optionalUuid(),
    cislo: z.string().max(50, "Číslo musí být 50 znaků nebo méně").optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    /**
     * Jen po splatnosti: stav PO_SPLATNOSTI NEBO prošlé `splatnostDo` u
     * vystavené/odeslané (worker stav překlápí až dávkou, filtr nesmí čekat).
     */
    poSplatnosti: z.boolean().optional(),
  });

/** Non-paginated list faktur dle přihlášky. */
export const listFakturyByPrihlaskaSchema = z.object({
  prihlaskaId: z.string().uuid("Neplatné ID přihlášky"),
});

/** ID-only schema. */
export const fakturaIdSchema = z.object({
  id: z.string().uuid("Neplatné ID faktury"),
});

/** Stornování faktury. */
export const stornoFakturaSchema = z.object({
  id: z.string().uuid("Neplatné ID faktury"),
  reason: z.string().max(2000, "Důvod musí být 2000 znaků nebo méně").optional(),
});

export type IssueFakturaInput = z.infer<typeof issueFakturaSchema>;
export type MarkPaidInput = z.infer<typeof markPaidSchema>;
export type CreateDobropisInput = z.infer<typeof createDobropisSchema>;
export type ListFakturyInput = z.infer<typeof listFakturySchema>;
export type ListFakturyByPrihlaskaInput = z.infer<typeof listFakturyByPrihlaskaSchema>;
export type FakturaIdInput = z.infer<typeof fakturaIdSchema>;
export type StornoFakturaInput = z.infer<typeof stornoFakturaSchema>;

// --- Jirka backend merge (additive): výpočetní helpery pro auto-fakturaci (Sprint 44) ---

/** Datum splatnosti (YYYY-MM-DD): 14 dní před termínem, jinak dnes. */
export function computeDueDate(terminDatum: Date, now: Date = new Date()): string {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const diffMs = terminDatum.getTime() - now.getTime();
  const diffDays = diffMs / MS_PER_DAY;
  const due = diffDays > 14 ? new Date(terminDatum.getTime() - 14 * MS_PER_DAY) : new Date(now);
  return due.toISOString().slice(0, 10);
}

/** Licenční poplatek HCA International ze základu bez DPH (default 10 %). */
export function computeLicenseFee(castkaBezDph: number, licencePct = 10): number {
  const fee = (castkaBezDph * licencePct) / 100;
  return Math.round(fee * 100) / 100;
}

/** Počet dní splatnosti: 14 pokud je termín >14 dní daleko, jinak 0. */
export function computeSplatnostDni(terminDatum: Date, now: Date = new Date()): 0 | 14 {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const diffDays = (terminDatum.getTime() - now.getTime()) / MS_PER_DAY;
  return diffDays > 14 ? 14 : 0;
}
